import { createContext, useContext, useState } from "react";
import type { AiDietaryPreferences } from "../types/aiDietaryPreferences";
import { AIDietaryFilter } from "../components/AIDietaryFilter";

type AiDietaryPreferencesContextType = {
  aiDietaryPreferences: AiDietaryPreferences;
  openFilters: () => void;
  closeFilters: () => void;
  isOpen: boolean;
  resetPreferences: () => void;
};

export const AiDietaryPreferencesContext = createContext<
  AiDietaryPreferencesContextType | undefined
>(undefined);

const defaultPreferences: AiDietaryPreferences = {
  onlyMeat: false,
  excludeGluten: false,
  onlyFishSeafood: false,
  excludeMeat: false,
  onlyChicken: false,
  onlyVegetarian: false,
  onlyVegan: false,
  excludeChicken: false,
  excludeFishSeafood: false,
};

export const AiDietaryPreferencesProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [aiDietaryPreferences, setAiDietaryPreferences] =
    useState<AiDietaryPreferences>(defaultPreferences);

  const [isOpen, setIsOpen] = useState(false);

  const openFilters = () => {
    setIsOpen(true);
  };

  const closeFilters = () => {
    setIsOpen(false);
  };

  const resetPreferences = () => {
    setAiDietaryPreferences(defaultPreferences);
  };

  return (
    <AiDietaryPreferencesContext.Provider
      value={{
        aiDietaryPreferences,
        openFilters,
        closeFilters,
        isOpen,
        resetPreferences,
      }}
    >
      {children}
      <AIDietaryFilter
        isOpen={isOpen}
        onClose={closeFilters}
        onFilterChange={setAiDietaryPreferences}
      />
    </AiDietaryPreferencesContext.Provider>
  );
};

export const useAiDietaryPreferences = () => {
  const context = useContext(AiDietaryPreferencesContext);

  if (!context) {
    throw new Error(
      "useAiDietaryPreferences must be used within an AiDietaryPreferencesProvider"
    );
  }

  return context;
};
